/**
 * The project's Quality tab (design "Flow Project Tabs and Quality", boards 4-6): is the project working, safe and
 * provable, and what blocks Publish. Its views: the overview (QualityOverview: score, release gate, the cards), Tests
 * and coverage (QualityTests), a check's findings (QualityCheck) and Settings (QualitySettings), the last three under a
 * "Quality / …" breadcrumb. Editors get Run all checks (and Run per check); while a run goes on, its progress per check
 * and Cancel, the overview read again every 2 s (else never by itself). In the workspace a sub-view's title is the tab
 * title's last crumb ("<Project> / Verify / Quality / API contract", `onSubPage`), a page opened from another one with
 * that one before it as a link ("Quality / Tests and coverage / 16:06 · manual", the view's `trail`), and "Checks of"
 * sits at that line's right end (`titleTools`). After a run, an ignore rule or a settings save it
 * sends `flow:quality-changed` (the tab row's count and the Publish dialog follow). A service never started since the
 * sandbox woke cannot run its tests: "Start and test" (Tests and coverage) and, while services are stopped, "Start all
 * and run all checks" start them, wait for their dependencies, then run (the progress shows while they start). The
 * page stops waiting (startWatch.js) when the project is stopped (Stop all: the banner, its chips and the busy buttons
 * go, and a run that would start from it anyway is cancelled) or a service never gets ready (a CLI that exits): it
 * names that service and runs the checks of the ones ready.
 * Viewers see everything, without the
 * run, ignore or edit controls. "Fix with AI" writes a concrete prompt into the project chat (`onAskAI`); file links
 * open the code at the line, as the Traces tab does.
 * On the overview the title's last crumb is its section picker ("Quality / Overview ▾", QualitySectionCrumb in
 * `titleCrumb`): it names the section in view while the page scrolls and scrolls to the one picked (Settings opens
 * Settings); Start all and run all checks, Run all checks and Settings sit in the title's right end with "Checks of".
 * Alone (no workspace title) the sections are a row of links above the overview, the buttons beside them. "Can I prove
 * it?" opens Build provenance (QualityProvenance, on a release) and Compliance mapping (QualityCompliance); a gate rule
 * on `provenance` opens Build provenance too. Compliance assessments (QualityAssessments: GDPR, NIS2, DORA, AI Act)
 * open from their card or a gate rule `assessment-<id>`; their evidence links open Code, Data and Traces (`onOpenView`).
 * With more than one service, "Checks of: All services / <service>" (QualityScope) reads the overview, the tests and
 * a check's findings of one service, and Run then runs on that service only; the services whose Deploy is off (Apps)
 * are named there: still checked, never blocking Publish.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { GearSix, Play, X } from '@phosphor-icons/react';
import {
  cancelLockfiles, cancelRun, cancelStartAndTest, complianceReport, createLockfiles, downloadAttestation, downloadSbom, getAccessibilityTargets, getAssessments, getOverview, notifyQualityChanged, postAccessibilityResults, saveSettings, startAndTest,
  startRun,
} from '../../services/QualityService';
import useProjectServices from '../../hooks/useProjectServices';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { isRunning } from '../../services/SandboxService';
import { runAccessibility } from './a11yRunner';
import { SANDBOX_OUTDATED } from '../../services/DataService';
import { codeSearch } from '../traces/tracesModel';
import QualityOverview from './QualityOverview';
import QualityTests from './QualityTests';
import QualitySettings from './QualitySettings';
import QualityCheck from './QualityCheck';
import QualityProvenance from './QualityProvenance';
import QualityCompliance from './QualityCompliance';
import QualityAssessments from './QualityAssessments';
import QualitySectionMenu, { QualitySectionCrumb } from './QualitySectionMenu';
import QualityRunDetail from './QualityRunDetail';
import QualityCoverage, { CoverageFile } from './QualityCoverage';
import { openPrintableReport } from './QualityProveCards';
import { Banner, QualitySubPageContext, RunNote, State } from './QualityCommon';
import { LockfileStatus } from './QualityLockfiles';
import QualityScope from './QualityScope';
import { PROJECT_STOPPED_EVENT, startVerdict } from './startWatch';
import { CHECK_IDS, checkTitle, errorText, plural, runErrorText, runsInBrowser } from './qualityModel';
import useTabSelectionUrl from '../../hooks/useTabSelectionUrl';
import { paramsOfView } from '../../utils/workspaceModel';
import './Quality.css';

export const RUN_POLL_MS = 2000;

/** The run in progress: the sandbox's checks and, when it runs, the accessibility check in this browser. */
export function mergeRunning(running, a11y) {
  if (!running && !a11y) { return null; }
  const checks = { ...(running?.checks || {}) };
  if (a11y) { checks.accessibility = a11y.done ? 'done' : 'running'; }
  return { ...(running || { trigger: 'manual', current: null }), checks, a11y: a11y || null };
}

function RunProgress({ running, canEdit, onCancel, cancelling }) {
  const states = running.checks || {};
  const ids = CHECK_IDS.filter((id) => states[id]);
  const done = ids.filter((id) => states[id] === 'done').length;
  const current = running.current;
  const a11y = running.a11y;
  const now = current?.check ? `${checkTitle(current.check)}${current.service ? ` (${current.service})` : ''}` : (a11y && !a11y.done ? `Accessibility${a11y.service ? ` (${a11y.service})` : ''}` : '');
  // How long it has been running, ticking each second (2026-10-31: "Running 0 of 1 area" stood still for a minute).
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = running.startedAt ? Math.max(0, Math.round((Date.now() - running.startedAt) / 1000)) : null;
  const elapsed = seconds === null ? '' : seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, '0')} s`;
  return (
    <div aria-live="polite" className="quality-progress" data-testid="quality-progress" role="status">
      <span className="quality-spinner" aria-hidden />
      <span className="quality-progress-text">
        <strong>{running.trigger === 'publish' ? 'Checking before Publish' : `Running ${done} of ${plural(ids.length, 'area')}…`}</strong>
        {running.trigger === 'publish' ? ` · ${done} of ${ids.length} done` : ''}
        {now ? ` · now ${now}` : ''}
        {elapsed ? <span className="quality-progress-elapsed" data-testid="quality-progress-elapsed">{` · ${elapsed}`}</span> : null}
      </span>
      {/* Nothing done yet: a moving bar instead of an empty one. */}
      <span aria-hidden className={`quality-progress-bar${done === 0 ? ' is-indeterminate' : ''}`}><span style={done === 0 ? undefined : { width: `${Math.round((100 * done) / ids.length)}%` }} /></span>
      <ul aria-label="Checks" className="quality-progress-list">
        {ids.map((id) => <li className={`is-${states[id]}`} data-testid={`quality-progress-${id}`} key={id}>{checkTitle(id)}</li>)}
      </ul>
      {canEdit && <button className="quality-btn is-sm" disabled={cancelling} onClick={onCancel} type="button"><X aria-hidden size={12} />{cancelling ? 'Cancelling…' : 'Cancel'}</button>}
    </div>
  );
}

RunProgress.propTypes = { canEdit: PropTypes.bool, cancelling: PropTypes.bool, onCancel: PropTypes.func.isRequired, running: PropTypes.object.isRequired };

// The states of the sandbox's Create lockfiles job while it works; the dependency checks with the lockfile banner.
const LOCKFILE_ACTIVE = new Set(['starting', 'creating']);
const LOCKFILE_CHECKS = ['dependencies', 'vulnerabilities', 'sbom'];
const START_STATES = { waiting: 'waiting', starting: 'starting…', ready: 'ready', failed: 'could not start' };

/** The page stopped waiting for a start (startWatch.js `startVerdict`): why, and what runs instead. */
export function StartGaveUp({ gaveUp, onDismiss }) {
  const names = gaveUp.notStarted.map((s) => s.service);
  const why = gaveUp.notStarted.filter((s) => s.why !== 'not started').map((s) => `${s.service}: ${s.why}`).join('; ');
  if (gaveUp.kind === 'stopped') {
    return (
      <Banner onDismiss={onDismiss} role="status" testId="quality-start-stopped" tone="info">
        <span>The start was stopped: the project&apos;s services no longer run, so no check runs. Start them again to run the checks.</span>
      </Banner>
    );
  }
  return (
    <Banner onDismiss={onDismiss} role="alert" testId="quality-start-gave-up" tone="warn">
      <span>
        {names.join(', ')} did not start{why ? ` (${why})` : ''}: {gaveUp.kind === 'partial'
          ? `the checks of ${gaveUp.ready.join(', ')} run without ${names.length === 1 ? 'it' : 'them'}.`
          : 'no check runs. Look at its logs, or start it from Apps.'}
      </span>
    </Banner>
  );
}

StartGaveUp.propTypes = { gaveUp: PropTypes.object.isRequired, onDismiss: PropTypes.func.isRequired };

/** "Start and test" while it starts the services (then the run's own progress shows), or why it stopped. */
export function StartProgress({ starting, onDismiss }) {
  const names = starting.services.map((s) => s.service);
  const then = starting.checks === 'all' ? 'every check runs' : 'their tests run';
  if (starting.state === 'failed') {
    return (
      <Banner onDismiss={onDismiss} role="alert" testId="quality-starting-failed" tone="warn">
        <span>Could not start and test: {starting.error}</span>
      </Banner>
    );
  }
  return (
    <div aria-live="polite" className="quality-progress" data-testid="quality-starting" role="status">
      <span className="quality-spinner" aria-hidden />
      <span className="quality-progress-text">
        <strong>Starting {names.join(', ') || 'the services'}…</strong> Once their dependencies are installed, {then}.
      </span>
      <ul aria-label="Services" className="quality-progress-list">
        {starting.services.map((s) => <li className={`is-${s.state === 'ready' ? 'done' : s.state === 'failed' ? 'failed' : 'running'}`} key={s.service}>{s.service} {START_STATES[s.state] || s.state}</li>)}
      </ul>
    </div>
  );
}

StartProgress.propTypes = { onDismiss: PropTypes.func.isRequired, starting: PropTypes.object.isRequired };

export default function QualityPanel({
  projectId, onAskAI = null, focus = null, onPublish = null, onOpenView = null, onSubPage = null, titleTools = null, titleCrumb = null,
}) {
  const navigate = useNavigate();
  const shows = usePlanTab();
  const location = useLocation();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => (focus?.sub ? { name: focus.sub, check: focus.check || null, id: focus.id || null, path: focus.path || null, framework: focus.framework || null } : { name: 'overview' }));
  const [notice, setNotice] = useState(null);
  // Why the checks did not start, where it was clicked (`origin`: 'all', 'start-all', 'overview:card:<id>',
  // 'overview:step:<key>', 'overview:interrupted', 'check:<id>', 'tests', 'assessments'): `{ origin, text }`.
  const [runNote, setRunNote] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [a11y, setA11y] = useState(null);
  // Create lockfiles: where it was clicked (`origin`: 'overview:step:<key>', 'overview:card:<id>', 'check:<id>'), the
  // call under way (`busy`), the sandbox's job it started (`jobId`); its result (`lockfileNote`) shows only there.
  const [lockfileRun, setLockfileRun] = useState(null);
  const [lockfileNote, setLockfileNote] = useState(null);
  const [gateSignal, setGateSignal] = useState(0);
  const [dismissedStart, setDismissedStart] = useState(null);
  // "Checks of": '' every service, else one service's checks (the gate stays the project's).
  const [scope, setScope] = useState(focus?.service || '');
  const projectServices = useProjectServices(projectId, { intervalMs: 10000 });
  const a11yAbort = useRef(null);
  // The sub page shown (its title, from SubHead): a page opened from it puts it in its trail.
  const shownSub = useRef(null);
  const wasRunning = useRef(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next = await (scope ? getOverview(projectId, { service: scope }) : getOverview(projectId));
      if (mine !== seq.current) { return; }
      setOverview(next);
      setError(null);
    } catch (e) {
      if (mine === seq.current) { setError(e); }
    }
  }, [projectId, scope]);

  useEffect(() => { load(); }, [load]);

  // Every assessment, on or off (the card's four gauges): read with the overview, kept when it cannot be read.
  const [assessmentList, setAssessmentList] = useState(null);
  const hasAssessments = Boolean(overview?.assessments);
  // Read again when the ones on, or their scores, change (not on every 2 s refresh of a run).
  const assessmentsKey = (overview?.assessments?.frameworks || []).map((f) => `${f.id}:${f.score ?? ''}:${f.totals?.gap ?? ''}`).join(',');
  useEffect(() => {
    if (!hasAssessments) { return undefined; }
    let live = true;
    (async () => {
      try {
        const answer = await getAssessments(projectId);
        if (live) { setAssessmentList(answer?.frameworks || null); }
      } catch { /* the card shows what the overview knows */ }
    })();
    return () => { live = false; };
  }, [projectId, hasAssessments, assessmentsKey]);
  useEffect(() => () => a11yAbort.current?.abort(), []);

  // Opened from the Publish dialog ("See in Quality"): the overview, on the gate and the readiness items.
  useEffect(() => {
    if (focus?.section === 'gate') {
      setView({ name: 'overview' });
      setGateSignal((n) => n + 1);
    }
  }, [focus]);

  // The page in the URL (`?tab=quality&check=<check id | page>&finding=<run id | file | framework>`, `&service=` for
  // "Checks of"): the overview and every service are not written; a check that does not exist is the overview.
  const knownCheck = view.name !== 'check' || CHECK_IDS.includes(view.check);
  useEffect(() => { if (!knownCheck) { setView({ name: 'overview' }); } }, [knownCheck]);
  useTabSelectionUrl('quality', paramsOfView({ tab: 'quality', focus: { ...view, sub: view.name, service: scope || null } }), { ready: knownCheck });

  const running = overview?.running || null;
  const told = overview?.starting || null;
  // The start the page stopped waiting for (`{ id, kind, ready, notStarted }`) and the one Stop all stopped (its id).
  const [gaveUp, setGaveUp] = useState(null);
  const [stoppedStart, setStoppedStart] = useState(null);
  const toldRef = useRef(told);
  toldRef.current = told;
  const cancelledStart = useRef(null);
  useEffect(() => {
    const stopped = (event) => {
      if (event.detail?.projectId !== projectId) { return; }
      if (toldRef.current?.state === 'starting') { setStoppedStart(toldRef.current.id); }
      projectServices.refresh?.();
    };
    window.addEventListener(PROJECT_STOPPED_EVENT, stopped);
    return () => window.removeEventListener(PROJECT_STOPPED_EVENT, stopped);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const verdict = gaveUp?.id === told?.id ? null : startVerdict(told, projectServices, Date.now(), { stoppedByEvent: Boolean(told) && stoppedStart === told.id });
  useEffect(() => {
    if (!verdict) { return; }
    setGaveUp({ id: told.id, ...verdict });
    // The sandbox stops waiting too: it would otherwise start its own run later (or refuse the next one as busy).
    if (overview?.canEdit) { cancelStartAndTest(projectId).catch(() => {}); }
    // The services already ready are checked without the ones that did not start.
    if (verdict.kind === 'partial' && overview?.canEdit) {
      startRun(projectId, { trigger: 'manual', services: verdict.ready, ...(told.checks === 'all' ? {} : { checks: ['tests'] }) })
        .catch(() => {})
        .finally(() => load());
    }
  }, [verdict?.kind, told?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const gaveUpNow = Boolean(told) && gaveUp?.id === told.id;
  // A start that was stopped runs nothing afterwards: should the sandbox still start its run, it is cancelled.
  useEffect(() => {
    if (gaveUpNow && gaveUp.kind === 'stopped' && told.state === 'running' && running && cancelledStart.current !== told.id) {
      cancelledStart.current = told.id;
      cancelRun(projectId).catch(() => {}).finally(() => load());
    }
  }, [gaveUpNow, gaveUp, told, running, projectId, load]);
  const starting = gaveUpNow ? null : told;
  const startingNow = starting?.state === 'starting';
  // The sandbox's Create lockfiles that starts the services first (vfs-service lockfileStart.js).
  const lockfileJob = overview?.lockfileJob || null;
  const lockfileActive = LOCKFILE_ACTIVE.has(lockfileJob?.state);
  const lockfileBusy = Boolean(lockfileRun?.busy) || lockfileActive;
  // The job this page started ended: its result where it was clicked (the page shown since then may be another one).
  useEffect(() => {
    if (!lockfileRun?.jobId || lockfileJob?.id !== lockfileRun.jobId || lockfileActive) { return; }
    const job = lockfileJob;
    setLockfileNote({
      origin: lockfileRun.origin, results: job.results || [], created: job.created || 0, refreshed: job.refreshed || 0, rerun: Boolean(job.runId),
      cancelled: job.state === 'cancelled', error: job.state === 'failed' ? (job.error || 'it stopped') : null,
    });
    setLockfileRun(null);
    if (job.created || job.refreshed) { notifyQualityChanged(projectId); }
  }, [lockfileJob?.id, lockfileJob?.state, lockfileRun?.jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Another page: the result of Create lockfiles belongs to where it was clicked, never to the next page.
  const viewKey = `${view.name}:${view.check || ''}`;
  useEffect(() => { setLockfileNote(null); setRunNote(null); }, [viewKey]);
  // While a run goes on (or services start for one): the overview every 2 s. When it ends, everyone else hears of it.
  useEffect(() => {
    // While the sandbox still starts, even after the page stopped waiting (a run it starts anyway is cancelled).
    if ((told?.state === 'starting' || lockfileActive) && !running) {
      const timer = setInterval(load, RUN_POLL_MS);
      return () => clearInterval(timer);
    }
    if (!running) {
      if (wasRunning.current) { wasRunning.current = false; setCancelling(false); notifyQualityChanged(projectId); }
      return undefined;
    }
    wasRunning.current = true;
    const timer = setInterval(load, RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [running, told?.state, lockfileActive, load, projectId]);

  const canEdit = Boolean(overview?.canEdit);
  const liveRun = mergeRunning(running, a11y);
  // The project's services that do not run now (the runner's list; unknown on an older sandbox: none).
  const stopped = projectServices.known
    ? (overview?.detected?.services || []).map((s) => s.service).filter((name) => !projectServices.services.some((s) => s.serviceName === `services/${name}` && isRunning(s)))
    : [];
  const publish = onPublish || (() => window.dispatchEvent(new CustomEvent('flow:open-publish', { detail: { projectId } })));

  /** The accessibility check, here in the browser: the running web previews, axe-core on each, the results sent. */
  const runA11yInBrowser = async () => {
    if (a11y && !a11y.done) { return; }
    const controller = new AbortController();
    a11yAbort.current = controller;
    setA11y({ index: 0, total: 0, service: null, done: false });
    try {
      const { targets = [], skipped = [] } = await getAccessibilityTargets(projectId);
      setA11y({ index: 0, total: targets.length, service: targets[0]?.service || null, done: false });
      const result = targets.length
        ? await runAccessibility({ targets, signal: controller.signal, onProgress: ({ index, total, service }) => setA11y({ index, total, service, done: false }) })
        : { engine: { name: 'axe-core' }, pages: [], durationMs: 0 };
      if (!controller.signal.aborted) { await postAccessibilityResults(projectId, { ...result, skipped }); }
    } catch (e) {
      if (!controller.signal.aborted) { setNotice(`Accessibility could not run: ${errorText(e)}`); }
    } finally {
      setA11y(null);
      notifyQualityChanged(projectId);
      load();
    }
  };

  // Create lockfiles: the sandbox starts the services that need it (never started since it woke), runs each service's
  // own package manager (no AI) and re-runs the dependency checks. `origin`: where it was clicked, where it reports.
  const lockfiles = async (services = null, origin = 'overview:card:vulnerabilities') => {
    setLockfileRun({ origin, busy: true, jobId: null });
    setLockfileNote(null);
    try {
      const answer = await createLockfiles(projectId, Array.isArray(services) ? services : undefined, { start: true });
      if (answer?.lockfileJob) {
        // Services start first: the overview's `lockfileJob` says where it is; the result comes when it ends.
        await load();
        setLockfileRun({ origin, busy: false, jobId: answer.lockfileJob.id });
        return;
      }
      const results = answer.results || [];
      const written = results.some((r) => r.status === 'created' || r.status === 'refreshed');
      setLockfileNote({ origin, results, rerun: written, created: answer.created, refreshed: answer.refreshed });
      if (written) {
        notifyQualityChanged(projectId);
        // A sandbox from before runs no checks itself after making them: start them here.
        if (!answer.run) { await startRun(projectId, { trigger: 'manual', checks: ['dependencies', 'vulnerabilities', 'sbom'] }).catch(() => {}); }
      }
    } catch (e) {
      setLockfileNote({ origin, results: [], error: e.code === 'busy' ? 'the lockfiles are already being created: wait for it to end, or stop it' : errorText(e) });
    }
    setLockfileRun((now) => (now?.busy ? null : now));
    load();
  };
  const stopLockfiles = async () => {
    try { await cancelLockfiles(projectId); } catch (e) { setNotice(errorText(e)); }
    load();
  };
  /**
   * What Create lockfiles shows on the page `here` ('overview' or 'check:<id>'): its progress (also when started from
   * another page, at that page's lockfile place) and, only where it was clicked, its result. null: nothing to show.
   */
  const lockfileAt = (here) => {
    const origin = lockfileNote?.origin || lockfileRun?.origin || null;
    const mine = Boolean(origin) && (origin === here || origin.startsWith(`${here}:`));
    const note = mine ? lockfileNote : null;
    const busy = mine && Boolean(lockfileRun?.busy);
    const job = lockfileActive ? lockfileJob : null;
    if (!note && !busy && !job) { return null; }
    return { note, busy, job, at: mine ? origin : (here === 'overview' ? 'overview:card:vulnerabilities' : here) };
  };
  const lockfileStatus = (state) => (state ? <LockfileStatus onDismiss={() => setLockfileNote(null)} onStop={canEdit ? stopLockfiles : null} state={state} /> : null);

  // Why the checks did not start: next to the button pressed (`origin`), else (none given) at the top.
  const runFailed = (text, origin) => (origin ? setRunNote({ origin, text }) : setNotice(text));
  const run = async (options = {}, origin = null) => {
    setNotice(null);
    setRunNote(null);
    if (options.checks?.length === 1 && options.checks[0] === 'accessibility' && runsInBrowser(overview)) { runA11yInBrowser(); return; }
    try {
      const started = await startRun(projectId, { trigger: 'manual', ...options });
      if ((started?.browserChecks || []).includes('accessibility')) { runA11yInBrowser(); }
    } catch (e) {
      runFailed(runErrorText(e), origin);
    }
    load();
  };
  // With a service picked in "Checks of", a run is of that service only (unless it names its own services).
  const runScoped = (options = {}, origin = null) => run(scope && !options.services ? { ...options, services: [scope] } : options, origin);
  // The note of the button at `origin`, or null.
  const runNoteAt = (origin) => (runNote?.origin === origin ? <RunNote note={runNote} onDismiss={() => setRunNote(null)} pop={origin === 'all' || origin === 'start-all'} /> : null);
  // "Check the pages": in this browser, or (the Flow has the headless browser job) a sandbox run like any other check.
  const runA11y = () => (runsInBrowser(overview) ? runA11yInBrowser() : run({ checks: ['accessibility'] }));
  /** Start and test: the services (default: every one that needs it), then their tests, or every check. */
  const startThenRun = async ({ services = null, checks = 'tests' } = {}, origin = null) => {
    setNotice(null);
    setRunNote(null);
    setDismissedStart(null);
    try {
      await startAndTest(projectId, { services, checks });
    } catch (e) {
      if (e.code === 'nothing_to_start') { await run(checks === 'all' ? {} : { checks: ['tests'], ...(services ? { services } : {}) }, origin); return; }
      if (e.code === 'busy' && gaveUpNow) {
        runFailed('Your sandbox is still ending the start that was stopped: try again in a minute.', origin);
      } else if (e.code === 'busy') {
        runFailed('Services are already starting, or the checks already run: the progress is at the top of Quality.', origin);
      } else {
        runFailed(runErrorText(e), origin);
      }
    }
    load();
    projectServices.refresh();
  };
  const cancel = async () => {
    setCancelling(true);
    a11yAbort.current?.abort();
    try { if (overview?.running) { await cancelRun(projectId); } } catch (e) { setNotice(errorText(e)); setCancelling(false); }
    load();
  };
  const sbom = async (format) => {
    setNotice(null);
    try { await downloadSbom(projectId, format); } catch (e) { setNotice(`Could not download the SBOM: ${errorText(e)}`); }
  };
  const attestation = async (id) => {
    setNotice(null);
    try { await downloadAttestation(projectId, id); } catch (e) { setNotice(`Could not download the attestation: ${errorText(e)}`); }
  };
  const complianceJson = async () => {
    setNotice(null);
    try { await complianceReport(projectId, 'json'); } catch (e) { setNotice(`Could not download the compliance report: ${errorText(e)}`); }
  };
  const compliancePrint = async () => {
    setNotice(null);
    try { await openPrintableReport(projectId); } catch (e) { setNotice(`Could not open the printable report: ${errorText(e)}`); }
  };
  const changed = () => { notifyQualityChanged(projectId); load(); };
  const saveWhole = async (settings) => {
    try {
      await saveSettings(projectId, settings);
      changed();
    } catch (e) {
      setNotice(errorText(e));
    }
  };
  // "Open in Code" and the file links only when the person's plan shows Code.
  const openCode = shows('code') ? (code) => navigate(`${location.pathname}${codeSearch(window.location.search, code)}`) : null;
  /**
   * A view, from the one shown: from a sub page, that page joins the trail (the crumbs between "Quality" and the new
   * page's title); another page of the same kind (the previous run) keeps the trail; a page already in it goes back there.
   */
  const open = (next) => setView((now) => {
    if (next.name === 'overview' || now.name === 'overview') { return next; }
    const trail = now.trail || [];
    if (next.name === now.name) { return { ...next, trail }; }
    const at = trail.findIndex((step) => step.view.name === next.name);
    if (at >= 0) { return { ...next, trail: trail.slice(0, at) }; }
    const { trail: _, ...here } = now; // eslint-disable-line no-unused-vars
    return { ...next, trail: [...trail, { view: here, label: shownSub.current?.title || here.name }] };
  });
  const openProvenance = (id = null) => open({ name: 'provenance', id: id || null });
  const openCompliance = () => open({ name: 'compliance' });
  const openAssessments = (framework = null) => open({ name: 'assessments', framework: framework || null });
  const openCheck = (id, rule = null) => {
    if (id === 'provenance') { openProvenance(); return; }
    if (id === 'compliance') { openCompliance(); return; }
    if (id === 'assessments') { openAssessments(rule?.framework); return; }
    if (id === 'gate') { setView({ name: 'overview' }); setGateSignal((n) => n + 1); return; }
    open(id === 'tests' ? { name: 'tests' } : { name: 'check', check: id });
  };
  const back = () => setView({ name: 'overview' });
  const openSettings = () => setView({ name: 'settings' });

  // In the workspace the sub page's title goes to the title line, after the pages it was opened from (each a link).
  const trail = view.trail;
  const toCrumb = useMemo(() => (onSubPage ? (sub) => {
    shownSub.current = sub;
    const steps = (trail || []).map((step, index) => ({ label: step.label, onOpen: () => setView({ ...step.view, trail: trail.slice(0, index) }) }));
    onSubPage(sub ? { ...sub, ...(steps.length ? { trail: steps } : {}) } : null);
  } : null), [onSubPage, trail]);

  let body;
  if (error && !overview) {
    body = error.code === 'sandbox_outdated'
      ? <State testId="quality-outdated" title="Quality needs a newer sandbox">{SANDBOX_OUTDATED}</State>
      : error.status === 0
        ? <State title="Quality is not available here">{errorText(error)}</State>
        : <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load Quality" tone="bad">{errorText(error)}</State>;
  } else if (!overview) {
    body = <div className="quality-state" role="status">Loading the checks…</div>;
  } else if (overview.enabled === false) {
    body = <State title="Quality is off in this sandbox">{overview.reason || 'Your sandbox admin turned the Quality checks off.'}</State>;
  } else if (view.name === 'tests') {
    body = <QualityTests canEdit={canEdit} onAskAI={onAskAI} onBack={back} onOpenCode={openCode} onOpenCoverage={() => open({ name: 'coverage' })} onOpenCoverageFile={(path) => open({ name: 'coverage', path })} onOpenRun={(id) => open({ name: 'run', id })} onRun={(options) => runScoped(options, 'tests')} onSaveSettings={canEdit ? saveWhole : null} onStartAndTest={(services) => startThenRun({ services }, 'tests')} overview={{ ...overview, starting }} projectId={projectId} runNote={runNoteAt('tests')} />;
  } else if (view.name === 'settings') {
    const choices = [...new Set(Object.values(overview.checks || {}).flatMap((c) => (c.top || []).map((f) => f.rule)).filter(Boolean))];
    body = <QualitySettings canEdit={canEdit} findingChoices={choices} headless={overview.headless || null} onBack={back} onSaved={changed} projectId={projectId} />;
  } else if (view.name === 'run' && view.id) {
    body = <QualityRunDetail canEdit={canEdit} key={view.id} onAskAI={onAskAI} onBack={back} onOpenCode={openCode} onOpenRun={(id) => open({ name: 'run', id })} projectId={projectId} runId={view.id} />;
  } else if (view.name === 'coverage' && view.path) {
    body = <CoverageFile canEdit={canEdit} key={view.path} onAskAI={onAskAI} onBack={back} onOpenCode={openCode} path={view.path} projectId={projectId} runId={view.runId || null} />;
  } else if (view.name === 'coverage') {
    body = <QualityCoverage min={overview.settings?.gate?.coverage?.min ?? 70} onBack={back} onOpenFile={(path, runId) => open({ name: 'coverage', path, runId })} projectId={projectId} />;
  } else if (view.name === 'provenance') {
    body = <QualityProvenance focusId={view.id || null} key={view.id || 'all'} onBack={back} projectId={projectId} />;
  } else if (view.name === 'assessments') {
    body = (
      <QualityAssessments
        canEdit={canEdit}
        framework={view.framework || view.check || null}
        key={view.framework || view.check || 'assessments'}
        onAskAI={onAskAI}
        onBack={back}
        onChanged={changed}
        onOpenCheck={openCheck}
        onOpenCode={openCode}
        onOpenProvenance={openProvenance}
        onOpenView={onOpenView}
        onRun={(options) => run(options, 'assessments')}
        projectId={projectId}
        runNote={runNoteAt('assessments')}
      />
    );
  } else if (view.name === 'compliance') {
    body = <QualityCompliance onBack={back} onOpenCheck={openCheck} onOpenProvenance={openProvenance} projectId={projectId} />;
  } else if (view.name === 'check' && view.check) {
    const lockfileHere = LOCKFILE_CHECKS.includes(view.check) ? lockfileAt(`check:${view.check}`) : null;
    body = <QualityCheck canEdit={canEdit} checkId={view.check} key={`${view.check}:${scope}`} lockfileBusy={lockfileBusy} lockfileStatus={lockfileStatus(lockfileHere)} onAskAI={onAskAI} onBack={back} onCreateLockfiles={(services) => lockfiles(services, `check:${view.check}`)} onOpenCheck={openCheck} onOpenCode={openCode} onRun={(options) => runScoped(options, `check:${view.check}`)} onRunA11y={runA11y} onSaved={changed} overview={{ ...overview, running: liveRun, starting }} projectId={projectId} runNote={runNoteAt(`check:${view.check}`)} />;
  } else {
    // Compact, in the title's right end after "Checks of" (or beside the section links when there is no title).
    const actions = (
      <div className={`quality-head-actions${titleTools ? ' is-title' : ''}`} data-testid="quality-head-actions">
        {canEdit && stopped.length > 0 && (
          <button
            className="quality-btn is-sm"
            data-testid="quality-start-all"
            disabled={Boolean(liveRun) || startingNow}
            onClick={() => startThenRun({ checks: 'all' }, 'start-all')}
            title={`${stopped.join(', ')} ${stopped.length === 1 ? 'is' : 'are'} stopped: starts them, then runs every area`}
            type="button"
          >
            <Play aria-hidden size={12} />Start all and run all checks
          </button>
        )}
        {canEdit && (
          <button className="quality-btn is-sm is-primary" data-testid="quality-run-all" disabled={Boolean(liveRun)} onClick={() => runScoped({}, 'all')} title={scope ? `Runs every area on ${scope}; the other services keep their last results` : 'Runs every area: the sandbox\'s checks and, in this browser, accessibility'} type="button">
            <Play aria-hidden size={12} weight="fill" />{scope ? `Run all checks on ${scope}` : 'Run all checks'}
          </button>
        )}
        <button className="quality-btn is-sm" data-testid="quality-open-settings" onClick={openSettings} type="button"><GearSix aria-hidden size={13} />Settings</button>
        {runNoteAt('all') || runNoteAt('start-all')}
      </div>
    );
    body = (
      <>
        {/* The tab's title ("Project / Verify / Quality / <Section> ▾") and its tools are the workspace's (WorkspaceView). */}
        {titleCrumb && <QualitySectionCrumb onOpenSettings={openSettings} slot={titleCrumb} watch={overview} />}
        {titleTools ? createPortal(actions, titleTools) : (
          <div className="quality-head is-main" data-testid="quality-head-main">
            <QualitySectionMenu onOpenSettings={openSettings} watch={overview} />
            {actions}
          </div>
        )}
        <QualityOverview
          a11yBusy={Boolean(a11y)}
          assessmentList={assessmentList}
          canEdit={canEdit}
          gateSignal={gateSignal}
          lockfileBusy={lockfileBusy}
          lockfileStatus={lockfileAt('overview')}
          onAskAI={onAskAI}
          onCreateLockfiles={(origin) => lockfiles(null, origin)}
          onDismissLockfiles={() => setLockfileNote(null)}
          onStopLockfiles={canEdit ? stopLockfiles : null}
          onDownloadAttestation={attestation}
          onDownloadCompliance={complianceJson}
          onDownloadSbom={sbom}
          onOpenCheck={openCheck}
          onOpenAssessments={openAssessments}
          onOpenCompliance={openCompliance}
          onOpenProvenance={openProvenance}
          onPrintCompliance={compliancePrint}
          onOpenSettings={openSettings}
          onPublish={publish}
          onRunA11y={runA11y}
          onRunAll={(origin) => runScoped({}, origin || 'all')}
          onRunCheck={(id, origin) => runScoped({ checks: [id] }, origin || `overview:card:${id}`)}
          onRetryInterrupted={(checks) => runScoped(checks.length ? { checks } : {}, 'overview:interrupted')}
          runNote={runNote}
          onDismissRunNote={() => setRunNote(null)}
          overview={{ ...overview, running: liveRun, starting }}
        />
      </>
    );
  }

  return (
    <QualitySubPageContext.Provider value={toCrumb}>
    <div className="quality-panel" data-testid="quality-panel">
      {liveRun && <RunProgress canEdit={canEdit} cancelling={cancelling} onCancel={cancel} running={liveRun} />}
      {!liveRun && starting && starting.state !== 'running' && dismissedStart !== starting.id && <StartProgress onDismiss={() => setDismissedStart(starting.id)} starting={starting} />}
      {gaveUpNow && dismissedStart !== gaveUp.id && <StartGaveUp gaveUp={gaveUp} onDismiss={() => setDismissedStart(gaveUp.id)} />}
      {notice && <p className="quality-banner is-warn" role="alert">{notice}</p>}
      {error && overview && <p className="quality-muted quality-small" role="status">Could not refresh: {errorText(error)}</p>}
      {overview && overview.enabled !== false && ['overview', 'tests', 'check'].includes(view.name) && (
        <QualityScope
          off={overview.deploy?.off || []}
          onChange={setScope}
          onOpenApps={onOpenView ? (service) => onOpenView({ tab: 'apps', focus: { service } }) : null}
          service={scope}
          services={(overview.detected?.services || []).map((s) => s.service)}
          slot={titleTools}
        />
      )}
      {body}
    </div>
    </QualitySubPageContext.Provider>
  );
}

QualityPanel.propTypes = {
  // `{ sub: 'tests' | 'settings' | 'check' | 'provenance' | 'compliance' | 'assessments', check?, id? }`: the view to
  // open on (for assessments, `check` is the framework);
  // `{ section: 'gate' }`: the overview's gate; `service`: "Checks of" that service.
  focus: PropTypes.object,
  // () => void: opens the Publish dialog ("All clear. Publish is open."); default: the `flow:open-publish` event.
  onPublish: PropTypes.func,
  // (prompt) => void: the project chat (App's askAIInProjectChat), asked to fix what failed.
  onAskAI: PropTypes.func,
  // ({ tab, focus }) => void: another workspace view (an assessment's evidence in Data or Traces); none: plain text.
  onOpenView: PropTypes.func,
  // ({ title, hint?, mono?, onUp, trail? } | null) => void: the sub-view shown, the workspace title's last crumb (WorkspaceView),
  // `trail` the pages it was opened from (`[{ label, onOpen }]`);
  // none: each sub-view has its own "← Quality" and title.
  onSubPage: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // The workspace title's slot after "Quality" (WorkspaceTitle `crumbRef`): the overview's section picker goes there.
  titleCrumb: PropTypes.object,
  // The workspace title line's right end (WorkspaceTitle `toolsRef`): "Checks of" and the overview's Run all checks,
  // Start all and Settings go there; none: above the view.
  titleTools: PropTypes.object,
};
